// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * Dialog for creating or editing a dataset definition.
 */
import { useCallback, useEffect, useMemo, useState, type FC } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { useServices } from '@/app-shell/services'
import { useKnowledgeBaseInstances } from '@/pipeline-nodes/analysis/shared/hooks/useKnowledgeBaseInstances'
import type { DatasetDefinition } from '../types'

const NO_KB_VALUE = '__none__'

export interface DatasetEditData {
  key: string
  label: string
  description?: string | null
  schemaId?: string | null
  knowledgeBase?: string | null
  systemPrompt?: string | null
  knowledgeBaseBatchSize?: number | null
  pageMode?: boolean
  diffMode?: boolean
}

interface DatasetEditDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  dataset?: DatasetDefinition | null
  onSave: (data: DatasetEditData) => void
  saving?: boolean
  readonly?: boolean
  /** Org/ws for KB instance lookups (prompt preview). */
  kbOrgId?: string
  kbWsId?: string
  /** When true, show a dropdown of real KB instances in the workspace.
   *  When false (default), the KB field is freeform text — used for
   *  app/system datasets where the value is a symbolic reference
   *  resolved against whatever workspace runs the dataset. */
  kbPicker?: boolean
}

export const DatasetEditDialog: FC<DatasetEditDialogProps> = ({
  open,
  onOpenChange,
  dataset,
  onSave,
  saving,
  readonly,
  kbOrgId,
  kbWsId,
  kbPicker = false,
}) => {
  const services = useServices()
  const isNew = !dataset
  const [key, setKey] = useState('')
  const [label, setLabel] = useState('')
  const [description, setDescription] = useState('')
  const [schemaId, setSchemaId] = useState('')
  const [knowledgeBase, setKnowledgeBase] = useState('')
  const [systemPrompt, setSystemPrompt] = useState('')
  const [batchSize, setBatchSize] = useState('')
  const [pageMode, setPageMode] = useState(true)
  const [diffMode, setDiffMode] = useState(false)
  const [showPromptPreview, setShowPromptPreview] = useState(false)

  // Fetch available schemas
  const { data: schemas = [] } = useQuery({
    queryKey: ['schema-catalog'],
    queryFn: () => services.schemaCatalog.fetch(),
    enabled: open,
    staleTime: 5 * 60 * 1000,
  })

  const { data: kbInstances, loading: kbLoading } = useKnowledgeBaseInstances({
    services,
    orgId: kbPicker ? kbOrgId : undefined,
    workspaceId: kbPicker ? kbWsId : undefined,
  })

  // Fetch analysis prompt rules (KB citation, page markers) for prompt preview
  const { data: analysisSettings } = useQuery({
    queryKey: ['analysis-settings'],
    queryFn: async () => {
      const { data } = await services.getClient().get<{
        kbCitationRules: string | null
        pageMarkerRules: string | null
        pageModeRules: string | null
        diffModeRules: string | null
      }>('/admin/analysis-settings')
      return data
    },
    staleTime: 5 * 60 * 1000,
    enabled: showPromptPreview,
  })

  // Fetch KB injection template + item schema id for prompt preview
  const kbInstanceId = knowledgeBase.trim()
  const { data: kbConfig } = useQuery({
    queryKey: ['kb-config', kbOrgId, kbWsId, kbInstanceId],
    queryFn: async () => {
      const { data } = await services.getClient().get<{
        settings?: { injectionTemplate?: string; itemSchemaId?: string | null }
      }>(`/org/${kbOrgId}/ws/${kbWsId}/knowledge_base/instances/${kbInstanceId}/knowledge-base`)
      return data
    },
    staleTime: 5 * 60 * 1000,
    enabled: showPromptPreview && !!kbOrgId && !!kbWsId && !!kbInstanceId,
  })

  // Fetch the KB item schema so we can show the field-meanings preface the
  // backend prepends to KB items at injection time.
  const kbItemSchemaId = kbConfig?.settings?.itemSchemaId ?? null
  const { data: kbItemSchema } = useQuery({
    queryKey: ['kb-item-schema', kbItemSchemaId],
    queryFn: async () => {
      const { data } = await services.getClient().get<{
        schemaId: string
        label: string
        fields: Array<{ key: string; label: string; description?: string | null }>
      }>(`/knowledge_base/schemas/${encodeURIComponent(kbItemSchemaId as string)}`)
      return data
    },
    staleTime: 5 * 60 * 1000,
    enabled: showPromptPreview && !!kbItemSchemaId,
  })

  useEffect(() => {
    if (open && dataset) {
      setKey(dataset.key)
      setLabel(dataset.label)
      setDescription(dataset.description ?? '')
      setSchemaId(dataset.schemaId ?? '')
      setKnowledgeBase(dataset.knowledgeBase ?? '')
      setSystemPrompt(dataset.systemPrompt ?? '')
      setBatchSize(
        dataset.knowledgeBaseBatchSize != null ? String(dataset.knowledgeBaseBatchSize) : ''
      )
      setPageMode(dataset.pageMode ?? true)
      setDiffMode(dataset.diffMode ?? false)
    } else if (open && !dataset) {
      setKey('')
      setLabel('')
      setDescription('')
      setSchemaId('')
      setKnowledgeBase('')
      setSystemPrompt('')
      setBatchSize('')
      setPageMode(true)
      setDiffMode(false)
    }
  }, [open, dataset])

  const handleSave = useCallback(() => {
    if (!key.trim() || !label.trim()) return
    const trimmedBatch = batchSize.trim()
    const parsedBatch = trimmedBatch === '' ? null : Math.max(0, Math.floor(Number(trimmedBatch)))
    onSave({
      key: key.trim(),
      label: label.trim(),
      description: description.trim() || null,
      schemaId: schemaId.trim() || null,
      knowledgeBase: knowledgeBase.trim() || null,
      systemPrompt: systemPrompt.trim() || null,
      knowledgeBaseBatchSize: Number.isFinite(parsedBatch) ? parsedBatch : null,
      pageMode,
      diffMode,
    })
  }, [key, label, description, schemaId, knowledgeBase, systemPrompt, batchSize, pageMode, diffMode, onSave])

  const selectedSchema = schemas.find((s) => s.id === schemaId)
  const hasKb = !!knowledgeBase.trim()

  const injectionTemplate = kbConfig?.settings?.injectionTemplate || '{{ knowledge_base }}'

  const promptSections = useMemo(() => {
    const sections: { label: string; source: string; content: string }[] = []

    // System message sections
    if (systemPrompt.trim()) {
      sections.push({ label: 'System', source: 'Dataset', content: systemPrompt.trim() })
    }
    if (pageMode && analysisSettings?.pageModeRules) {
      sections.push({ label: 'System', source: 'Page mode rules', content: analysisSettings.pageModeRules })
    }
    if (diffMode && analysisSettings?.diffModeRules) {
      sections.push({ label: 'System', source: 'Diff mode rules', content: analysisSettings.diffModeRules })
    }
    if (hasKb && analysisSettings?.kbCitationRules) {
      sections.push({ label: 'System', source: 'KB citation rules', content: analysisSettings.kbCitationRules })
    }
    if (hasKb && analysisSettings?.pageMarkerRules) {
      sections.push({ label: 'System', source: 'Page marker rules', content: analysisSettings.pageMarkerRules })
    }
    if (selectedSchema?.promptTemplate) {
      sections.push({ label: 'System', source: `Schema: ${selectedSchema.label}`, content: selectedSchema.promptTemplate })
    }

    // User message: data only
    if (hasKb && kbItemSchema && kbItemSchema.fields?.length) {
      const lines = kbItemSchema.fields
        .filter(f => (f.description ?? '').trim().length > 0)
        .map(f => `- ${f.key}: ${(f.description ?? '').trim()}`)
      if (lines.length > 0) {
        sections.push({
          label: 'User',
          source: `KB field meanings (schema: ${kbItemSchema.label})`,
          content: `Each KB item below has a \`fields\` object. Field meanings:\n${lines.join('\n')}`,
        })
      }
    }
    if (hasKb) {
      sections.push({ label: 'User', source: 'KB injection template', content: injectionTemplate })
    }
    sections.push({ label: 'User', source: 'Document text', content: '{ document text }' })

    return sections
  }, [systemPrompt, pageMode, diffMode, hasKb, selectedSchema, analysisSettings, injectionTemplate, kbItemSchema])

  const formContent = (
    <div className="space-y-4 py-2">
      <div className="space-y-1.5">
        <Label htmlFor="ds-key">Key</Label>
        <Input
          id="ds-key"
          value={key}
          onChange={(e) => setKey(e.target.value)}
          placeholder="score_maryland_bill"
          disabled={!isNew || readonly}
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="ds-label">Label</Label>
        <Input
          id="ds-label"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="Score Maryland Bill"
          disabled={readonly}
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="ds-description">Description</Label>
        <Input
          id="ds-description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Optional description"
          disabled={readonly}
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="ds-schema">Schema</Label>
        <Select
          value={schemaId || undefined}
          onValueChange={setSchemaId}
          disabled={readonly}
        >
          <SelectTrigger id="ds-schema">
            <SelectValue placeholder="Select a schema..." />
          </SelectTrigger>
          <SelectContent>
            {schemas.map((s) => {
              const namespace = s.id.split('.')[0] ?? ''
              return (
                <SelectItem key={s.id} value={s.id}>
                  <span className="text-muted-foreground">{namespace}/</span>
                  {s.label}
                </SelectItem>
              )
            })}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="ds-kb">Knowledge Base Instance {kbPicker ? '' : 'ID'}</Label>
        {kbPicker ? (
          <Select
            value={knowledgeBase || NO_KB_VALUE}
            onValueChange={(v) => setKnowledgeBase(v === NO_KB_VALUE ? '' : v)}
            disabled={readonly}
          >
            <SelectTrigger id="ds-kb">
              <SelectValue placeholder={kbLoading ? 'Loading...' : 'Select a knowledge base...'} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NO_KB_VALUE}>
                <span className="text-muted-foreground">None</span>
              </SelectItem>
              {kbInstances.map((kb) => (
                <SelectItem key={kb.instanceId} value={kb.instanceId}>
                  {kb.label}
                  <span className="text-muted-foreground"> ({kb.instanceId})</span>
                </SelectItem>
              ))}
              {knowledgeBase && !kbInstances.some((kb) => kb.instanceId === knowledgeBase) && (
                <SelectItem value={knowledgeBase}>
                  {knowledgeBase}
                  <span className="text-muted-foreground"> (missing)</span>
                </SelectItem>
              )}
            </SelectContent>
          </Select>
        ) : (
          <Input
            id="ds-kb"
            value={knowledgeBase}
            onChange={(e) => setKnowledgeBase(e.target.value)}
            placeholder="kb.score-maryland-bill"
            disabled={readonly}
          />
        )}
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="ds-prompt">System Prompt</Label>
        <Textarea
          id="ds-prompt"
          value={systemPrompt}
          onChange={(e) => setSystemPrompt(e.target.value)}
          placeholder="System prompt providing domain context for LLM analysis..."
          rows={6}
          disabled={readonly}
        />
      </div>
      {hasKb && (
        <div className="space-y-1.5">
          <Label htmlFor="ds-batch-size">KB items per batch</Label>
          <Input
            id="ds-batch-size"
            type="number"
            min={0}
            step={1}
            className="max-w-[220px]"
            value={batchSize}
            onChange={(e) => setBatchSize(e.target.value)}
            placeholder="Auto (schema default)"
            disabled={readonly}
          />
          <p className="text-xs text-muted-foreground">
            Number of knowledge base items per LLM call. Leave blank for the schema-based default. Set to 0 to send all items in one request.
          </p>
        </div>
      )}
      <div className="flex items-start gap-3">
        <Checkbox
          id="ds-page-mode"
          checked={pageMode}
          onCheckedChange={(v) => setPageMode(Boolean(v))}
          disabled={readonly}
          aria-label="Analyze one page at a time"
        />
        <div className="space-y-1">
          <Label htmlFor="ds-page-mode" className="text-sm font-medium cursor-pointer">
            Analyze one page at a time
          </Label>
          <p className="text-xs text-muted-foreground">
            When enabled, each page is analyzed individually. When disabled, the full document is sent in one request.
          </p>
        </div>
      </div>
      <div className="flex items-start gap-3">
        <Checkbox
          id="ds-diff-mode"
          checked={diffMode}
          onCheckedChange={(v) => setDiffMode(Boolean(v))}
          disabled={readonly}
          aria-label="Compare document versions"
        />
        <div className="space-y-1">
          <Label htmlFor="ds-diff-mode" className="text-sm font-medium cursor-pointer">
            Version comparison mode
          </Label>
          <p className="text-xs text-muted-foreground">
            When enabled, pairs old and new document versions and sends a unified diff to the LLM.
          </p>
        </div>
      </div>
    </div>
  )

  const promptPreviewContent = (
    <div className="space-y-2 overflow-y-auto">
      {promptSections.map((section, i) => (
        <div key={i}>
          <div className="flex items-baseline gap-2 mb-1">
            <span className="text-xs font-medium text-muted-foreground">{section.label}</span>
            <span className="text-xs text-muted-foreground/60">{section.source}</span>
          </div>
          <pre className="p-3 text-xs bg-muted rounded-md whitespace-pre-wrap">{section.content}</pre>
        </div>
      ))}
    </div>
  )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={`max-h-[85vh] overflow-hidden flex flex-col ${showPromptPreview ? 'sm:max-w-3xl lg:max-w-[90vw]' : 'sm:max-w-3xl'}`}>
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle>{readonly ? dataset?.label : isNew ? 'New Dataset' : `Edit ${dataset?.label}`}</DialogTitle>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setShowPromptPreview((v) => !v)}
            >
              {showPromptPreview ? 'Hide' : 'View'} Full Prompt
            </Button>
          </div>
        </DialogHeader>
        {showPromptPreview ? (
          <div className="flex-1 grid grid-cols-1 lg:grid-cols-2 gap-6 min-h-0 overflow-hidden">
            <div className="overflow-y-auto pr-2">{formContent}</div>
            <div className="overflow-y-auto lg:border-l lg:pl-6">{promptPreviewContent}</div>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto">{formContent}</div>
        )}
        <DialogFooter>
          {readonly ? (
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Close
            </Button>
          ) : (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={!key.trim() || !label.trim() || !schemaId || saving}>
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
